import {
  Alert,
  Button,
  Card,
  CardBody,
  DateRangePicker,
  Dropdown,
  EmptyState,
  formatDate,
  IconButton,
  Inline,
  Money,
  Pagination,
  PayDatesChip,
  SearchInput,
  SegmentedControl,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableLoading,
  TableRow,
  Text,
  Toolbar,
  ToolbarCount,
  ToolbarItem,
  ToolbarSpacer,
  UploadStatusBadge,
  VisuallyHidden,
  type DropdownItem,
} from '@hkdevs/design-system';
import { useState } from 'react';
import {
  usePayDates,
  useUploads,
  type PayDateSummary,
  type UploadStatus,
  type UploadSummary,
} from '../../api/queries/uploads';
import { dollars } from '../lineItems/LineItemList';
import { toDate, toIso } from '../payHistory/PayHistoryView';
import { useStatusAnnouncer } from './announcer';
import { downloadLabel, isLegacy, LegacyBadge } from './legacyUpload';

export const PAGE_SIZE = 25;

/** The page's URL state (ADR 0023): the view, every filter and the page. */
export interface PayDataHistorySearch {
  /** By pay date; left out for By upload. */
  view?: 'byPayDate' | undefined;
  /** Contained in the file name (By pay date: a contributing upload's). */
  q?: string | undefined;
  /** By upload only. */
  status?: UploadStatus | undefined;
  /** Pay dates, inclusive, ISO; either end may be open. */
  from?: string | undefined;
  to?: string | undefined;
  /** 1-based; left out for the first. */
  page?: number | undefined;
}

const STATUSES: { value: 'all' | UploadStatus; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'processing', label: 'Processing' },
  { value: 'queued', label: 'Queued' },
  { value: 'rejected', label: 'Rejected' },
];

const VIEWS = [
  { value: 'byUpload', label: 'By upload' },
  { value: 'byPayDate', label: 'By pay date' },
];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function parsePayDataHistorySearch(search: Record<string, unknown>): PayDataHistorySearch {
  const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined);
  const date = (value: unknown) => {
    const iso = text(value);
    return iso && ISO_DATE.test(iso) ? iso : undefined;
  };
  const status = STATUSES.find((s) => s.value !== 'all' && s.value === search.status);
  const page = Number(search.page);
  return {
    view: search.view === 'byPayDate' ? 'byPayDate' : undefined,
    q: text(search.q),
    status: status ? (status.value as UploadStatus) : undefined,
    from: date(search.from),
    to: date(search.to),
    page: Number.isInteger(page) && page > 1 ? page : undefined,
  };
}

const plural = (count: number, one: string, many: string) =>
  `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`;

/** A value over a muted second line: the Uploaded column. */
function TwoLines({ first, second }: { first: string; second: string }) {
  return (
    <Stack as="span" gap={0}>
      <span>{first}</span>
      <Text tone="muted" size="meta">
        {second}
      </Text>
    </Stack>
  );
}

export interface PayHistoryUploadsViewProps {
  search: PayDataHistorySearch;
  onSearchChange: (next: PayDataHistorySearch) => void;
  /** Opens Upload files (the empty state's action). */
  onUpload: () => void;
  onDownload: (upload: UploadSummary) => void;
  /** A rejected upload's error report. */
  onOpenErrorReport: (upload: UploadSummary) => void;
  onDelete: (upload: UploadSummary) => void;
  /** Status changes are announced here; off while a dialog announces its own. */
  announce?: boolean;
}

/** The admin's Pay history uploads page body (pc-dbb.9): by upload or by pay date. */
export function PayHistoryUploadsView({
  search,
  onSearchChange,
  onUpload,
  onDownload,
  onOpenErrorReport,
  onDelete,
  announce = true,
}: PayHistoryUploadsViewProps) {
  const byUpload = search.view !== 'byPayDate';
  const page = search.page ?? 1;
  const q = search.q?.trim() || undefined;
  const uploads = useUploads({
    kind: 'pay_history',
    q,
    status: search.status,
    from: search.from,
    to: search.to,
    page: byUpload ? page : 1,
    pageSize: PAGE_SIZE,
  });
  const payDates = usePayDates(
    { q, from: search.from, to: search.to, page, pageSize: PAGE_SIZE },
    !byUpload,
  );
  const announcement = useStatusAnnouncer(uploads.data?.items);
  const [expanded, setExpanded] = useState<string | null>(null);

  const filtered = !!(q || search.status || search.from || search.to);
  /** New filters go back to the first page. */
  const set = (patch: Partial<PayDataHistorySearch>) =>
    onSearchChange({ ...search, ...patch, page: undefined });
  const clearFilters = () => onSearchChange({ view: search.view });
  const setPage = (next: number) =>
    onSearchChange({ ...search, page: next > 1 ? next : undefined });

  const status = <VisuallyHidden role="status">{announce ? announcement : ''}</VisuallyHidden>;

  // Nothing uploaded at all: just the empty state, as the screen has it.
  if (byUpload && !filtered && uploads.data?.total === 0) {
    return (
      <Card>
        <CardBody>
          <EmptyState
            title="Nothing uploaded yet"
            description="Upload pay history files and they’ll appear here, with the pay dates they cover."
            icon="upload"
            headingLevel={2}
            actions={
              <Button size="sm" leadingIcon="upload" onClick={onUpload}>
                Upload files
              </Button>
            }
          />
        </CardBody>
      </Card>
    );
  }

  const results = byUpload ? uploads : payDates;
  const total = results.data?.total;
  let body;
  if (results.isError) {
    body = (
      <Alert
        tone="danger"
        title={byUpload ? 'Uploads couldn’t be loaded' : 'Pay dates couldn’t be loaded'}
        action={
          <Button size="sm" variant="secondary" onClick={() => void results.refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  } else if (total === 0) {
    body = (
      <Card>
        <CardBody>
          {filtered ? (
            <EmptyState
              title={byUpload ? 'No matching uploads' : 'No matching pay dates'}
              description={
                byUpload
                  ? 'No uploads match your search or filters. Try a different file name, status, or date range.'
                  : 'No pay dates match your search or date range. Try a different file name or range.'
              }
              icon="search"
              headingLevel={2}
              actions={
                <Button size="sm" variant="secondary" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No pay dates yet"
              description="Pay dates appear here once an upload is processed."
              icon="calendar"
              headingLevel={2}
            />
          )}
        </CardBody>
      </Card>
    );
  } else {
    const pageCount = results.data
      ? Math.max(1, Math.ceil(results.data.total / results.data.pageSize))
      : 1;
    body = (
      <>
        <Card flush>
          {byUpload ? (
            <UploadsTable
              items={uploads.data?.items}
              busy={!uploads.data || (uploads.isFetching && uploads.isPlaceholderData)}
              onDownload={onDownload}
              onOpenErrorReport={onOpenErrorReport}
              onDelete={onDelete}
            />
          ) : (
            <PayDatesTable
              items={payDates.data?.items}
              busy={!payDates.data || (payDates.isFetching && payDates.isPlaceholderData)}
              expanded={expanded}
              onExpandedChange={setExpanded}
            />
          )}
        </Card>
        {results.data && pageCount > 1 && (
          <Pagination page={page} pageCount={pageCount} onPageChange={setPage} size="sm" />
        )}
      </>
    );
  }

  return (
    <Stack gap={3} shrink>
      <Toolbar aria-label="Filter uploads">
        <SegmentedControl
          aria-label="View"
          size="sm"
          options={VIEWS}
          value={byUpload ? 'byUpload' : 'byPayDate'}
          onChange={(next) =>
            onSearchChange({
              ...search,
              view: next === 'byPayDate' ? 'byPayDate' : undefined,
              // The status filter is By upload's.
              status: next === 'byPayDate' ? undefined : search.status,
              page: undefined,
            })
          }
        />
        <ToolbarItem size="search">
          <SearchInput
            label="Search uploads by file name"
            placeholder="Search by file name"
            value={search.q ?? ''}
            onValueChange={(next) => set({ q: next || undefined })}
          />
        </ToolbarItem>
        {byUpload && (
          <ToolbarItem size="filter">
            <Select
              aria-label="Status"
              options={STATUSES}
              value={search.status ?? 'all'}
              onChange={(event) => {
                const value = event.target.value as 'all' | UploadStatus;
                set({ status: value === 'all' ? undefined : value });
              }}
            />
          </ToolbarItem>
        )}
        <ToolbarItem size="range">
          <DateRangePicker
            aria-label="Pay dates"
            clearable
            allowTextInput
            value={{
              start: search.from ? toDate(search.from) : null,
              end: search.to ? toDate(search.to) : null,
            }}
            onChange={({ start, end }) =>
              set({ from: start ? toIso(start) : undefined, to: end ? toIso(end) : undefined })
            }
          />
        </ToolbarItem>
        <ToolbarSpacer />
        {total !== undefined && (
          <ToolbarCount>
            {byUpload ? plural(total, 'upload', 'uploads') : plural(total, 'pay date', 'pay dates')}
          </ToolbarCount>
        )}
      </Toolbar>
      {body}
      {status}
    </Stack>
  );
}

function UploadsTable({
  items,
  busy,
  onDownload,
  onOpenErrorReport,
  onDelete,
}: {
  items: readonly UploadSummary[] | undefined;
  busy: boolean;
  onDownload: (upload: UploadSummary) => void;
  onOpenErrorReport: (upload: UploadSummary) => void;
  onDelete: (upload: UploadSummary) => void;
}) {
  return (
    <Table aria-label="Uploads" responsive="cards" busy={busy}>
      <TableHead>
        <TableCell>File name</TableCell>
        <TableCell>Uploaded</TableCell>
        <TableCell>Pay dates</TableCell>
        <TableCell numeric>Records</TableCell>
        <TableCell numeric>Total</TableCell>
        <TableCell>Status</TableCell>
        <TableCell>
          <VisuallyHidden>Actions</VisuallyHidden>
        </TableCell>
      </TableHead>
      <TableBody>
        {!items ? (
          <TableLoading colSpan={7} rows={6} label="Loading uploads" />
        ) : (
          items.map((upload) => (
            <UploadRow
              key={upload.id}
              upload={upload}
              onDownload={onDownload}
              onOpenErrorReport={onOpenErrorReport}
              onDelete={onDelete}
            />
          ))
        )}
      </TableBody>
    </Table>
  );
}

function UploadRow({
  upload,
  onDownload,
  onOpenErrorReport,
  onDelete,
}: {
  upload: UploadSummary;
  onDownload: (upload: UploadSummary) => void;
  onOpenErrorReport: (upload: UploadSummary) => void;
  onDelete: (upload: UploadSummary) => void;
}) {
  const rejected = upload.status === 'rejected';
  const completed = upload.status === 'completed';
  const menu: DropdownItem[] = rejected
    ? [{ label: 'View error report', icon: 'info', onClick: () => onOpenErrorReport(upload) }]
    : completed
      ? [{ label: 'Delete upload', icon: 'trash', danger: true, onClick: () => onDelete(upload) }]
      : [];
  const status = (
    <UploadStatusBadge
      status={upload.status}
      {...(rejected && {
        onRejectedClick: () => onOpenErrorReport(upload),
        rejectedActionLabel: `Rejected, view the error report for ${upload.fileName}`,
      })}
    />
  );
  return (
    <TableRow>
      <TableCell rowHeader tone="mono">
        {upload.fileName}
      </TableCell>
      <TableCell label="Uploaded">
        <TwoLines first={formatDate(upload.uploadedAt, 'date')} second={upload.uploadedBy.name} />
      </TableCell>
      <TableCell label="Pay dates">
        <PayDatesChip dates={upload.payDates} />
      </TableCell>
      <TableCell label="Records" numeric tone="muted">
        {upload.records === null ? '—' : upload.records.toLocaleString('en-US')}
      </TableCell>
      <TableCell label="Total" numeric tone="strong">
        {upload.totalCents === null ? '—' : <Money value={dollars(upload.totalCents)} />}
      </TableCell>
      <TableCell label="Status">
        {isLegacy(upload) ? (
          <Inline as="span" gap={1}>
            {status}
            <LegacyBadge />
          </Inline>
        ) : (
          status
        )}
      </TableCell>
      <TableCell>
        <Inline as="span" gap={1} justify="end" wrap={false}>
          {completed && (
            <IconButton
              icon="download"
              size="sm"
              variant="ghost"
              aria-label={downloadLabel(upload)}
              onClick={() => onDownload(upload)}
            />
          )}
          {menu.length > 0 && (
            <Dropdown
              align="end"
              trigger={
                <IconButton
                  icon="more-vertical"
                  size="sm"
                  variant="ghost"
                  aria-label={`More actions for ${upload.fileName}`}
                />
              }
              items={menu}
            />
          )}
        </Inline>
      </TableCell>
    </TableRow>
  );
}

function PayDatesTable({
  items,
  busy,
  expanded,
  onExpandedChange,
}: {
  items: readonly PayDateSummary[] | undefined;
  busy: boolean;
  expanded: string | null;
  onExpandedChange: (payDate: string | null) => void;
}) {
  return (
    <Table aria-label="Pay dates" responsive="cards" busy={busy}>
      <TableHead>
        <TableCell>Pay date</TableCell>
        <TableCell numeric>Records</TableCell>
        <TableCell numeric>Total</TableCell>
        <TableCell>Uploads</TableCell>
        <TableCell width="44px">
          <VisuallyHidden>Details</VisuallyHidden>
        </TableCell>
      </TableHead>
      <TableBody>
        {!items ? (
          <TableLoading colSpan={5} rows={6} label="Loading pay dates" />
        ) : (
          items.map((row) => {
            const open = expanded === row.payDate;
            return (
              <TableRow
                key={row.payDate}
                label={`Uploads for ${formatDate(row.payDate)}`}
                expanded={open}
                onToggle={() => onExpandedChange(open ? null : row.payDate)}
                detail={<Contributions row={row} />}
              >
                <TableCell rowHeader tone="strong">
                  {formatDate(row.payDate)}
                </TableCell>
                <TableCell label="Records" numeric tone="muted">
                  {row.records.toLocaleString('en-US')}
                </TableCell>
                <TableCell label="Total" numeric tone="strong">
                  <Money value={dollars(row.totalCents)} />
                </TableCell>
                <TableCell label="Uploads" tone="muted">
                  {plural(row.contributions.length, 'upload', 'uploads')}
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}

/** A pay date's contributing uploads. */
function Contributions({ row }: { row: PayDateSummary }) {
  return (
    // Scrolls sideways when narrow: its long file names would overflow a card.
    <Table aria-label={`Contributing uploads for ${formatDate(row.payDate)}`}>
      <TableHead>
        <TableCell>Contributing upload</TableCell>
        <TableCell>Uploaded</TableCell>
        <TableCell numeric>Records</TableCell>
        <TableCell numeric>Amount</TableCell>
      </TableHead>
      <TableBody>
        {row.contributions.map((c) => (
          <TableRow key={c.uploadId}>
            <TableCell rowHeader tone="mono">
              {c.fileName}
            </TableCell>
            <TableCell label="Uploaded" tone="muted">
              {formatDate(c.uploadedAt, 'date')}
            </TableCell>
            <TableCell label="Records" numeric tone="muted">
              {c.records.toLocaleString('en-US')}
            </TableCell>
            <TableCell label="Amount" numeric>
              <Money value={dollars(c.amountCents)} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
